import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { timeAgo } from "./utils";

const Node = ({ label, sub, time, state, isLast, t }) => {
  const nodeColor = {
    done:     t.accent,
    active:   t.gold,
    pending:  "rgba(128,128,128,0.35)",
    rejected: "#c84040",
  }[state] || "rgba(128,128,128,0.35)";

  return (
    <Box sx={{ display: "flex", gap: "12px", minHeight: isLast ? "auto" : "56px" }}>

      <Box sx={{
        display: "flex", flexDirection: "column",
        alignItems: "center",
        width: "12px", flexShrink: 0,
      }}>
        <Box sx={{
          width: "12px", height: "12px",
          borderRadius: "50%", flexShrink: 0,
          mt: "2px",
          background: state === "pending" ? "transparent" : nodeColor,
          border: `2px solid ${state === "pending" ? "rgba(128,128,128,0.35)" : nodeColor}`,
        }} />
        {!isLast && (
          <Box sx={{
            width: "2px",
            flexGrow: 1,
            minHeight: "32px",
            mt: "4px",
            background: state === "done" ? t.accent : "rgba(128,128,128,0.25)",
          }} />
        )}
      </Box>

      <Box sx={{ pb: isLast ? "0" : "8px", flex: 1 }}>
        <Typography sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "12px", fontWeight: 500,
          color: state === "pending" ? t.text3 : t.text,
          mt: "1px",
        }}>
          {label}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: "11px", color: t.text3, mt: "1px", fontWeight: 300 }}>
            {sub}
          </Typography>
        )}
        {time && (
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", color: t.text3, mt: "3px",
          }}>
            {time}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ApprovalTimeline = ({ request }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const pmo = request.approvals?.find((a) => a.gate === "PMO");
  const scg = request.approvals?.find((a) => a.gate === "SCG");

  // Helper — status implies gates passed even if approvals array is empty
  const pastPmo = ["PMO_APPROVED", "ACTIVE", "ENDED"].includes(request.status);
  const pastScg = ["ACTIVE", "ENDED"].includes(request.status);

  const nodes = [
    {
      label: "Submitted by MAC Leader",
      sub:   request.submittedByName || "",
      time:  timeAgo(request.submittedAt),
      state: "done",
    },
    {
      label: pmo
        ? pmo.decision === "APPROVED" ? "PMO Gate — Approved" : "PMO Gate — Rejected"
        : pastPmo ? "PMO Gate — Approved"
        : request.status === "SUBMITTED" ? "PMO Gate — Pending"
        : "PMO Gate",
      sub: pmo
        ? `${pmo.decidedByName}${pmo.comments ? ` · "${pmo.comments}"` : ""}`
        : pastPmo ? "PMO approved"
        : request.status === "SUBMITTED" ? "Awaiting PMO review"
        : "",
      time:  pmo ? timeAgo(pmo.decidedAt) : "",
      state: pmo
        ? pmo.decision === "APPROVED" ? "done" : "rejected"
        : pastPmo ? "done"
        : request.status === "SUBMITTED" ? "active"
        : "pending",
    },
    {
      label: scg
        ? scg.decision === "APPROVED" ? "SCG Gate — Approved" : "SCG Gate — Rejected"
        : pastScg ? "SCG Gate — Approved"
        : request.status === "PMO_APPROVED" ? "SCG Gate — Pending"
        : "SCG Gate",
      sub: scg
        ? scg.decidedByName
        : pastScg ? "SCG approved"
        : request.status === "PMO_APPROVED" ? "Awaiting SCG review"
        : "Pending PMO approval",
      time:  scg ? timeAgo(scg.decidedAt) : "",
      state: scg
        ? scg.decision === "APPROVED" ? "done" : "rejected"
        : pastScg ? "done"
        : request.status === "PMO_APPROVED" ? "active"
        : "pending",
    },
    {
      label: request.status === "ENDED" ? "Allocation Ended" : "Allocation Active",
      sub:   request.status === "ENDED" ? "Allocation concluded" : pastScg ? "Associate deployed" : "",
      time:  "",
      state: pastScg ? "done" : "pending",
    },
  ];

  return (
    <Box>
      {nodes.map((node, i) => (
        <Node
          key={i}
          {...node}
          isLast={i === nodes.length - 1}
          t={t}
        />
      ))}
    </Box>
  );
};

export default ApprovalTimeline;